import type { BacktestMetrics } from "../stores/backtestStore";

interface MetricsGridProps {
  metrics?: BacktestMetrics | null;
  initialCapital?: number;
  finalValue?: number;
  totalSlippage?: number;
  totalRegulatoryFees?: number;
}

interface MetricCardProps {
  label: string;
  value: string;
  isPositive?: boolean | null;
}

function MetricCard({ label, value, isPositive }: MetricCardProps) {
  const valueClass =
    isPositive === null
      ? "text-surface-100"
      : isPositive
        ? "metric-value-positive"
        : "metric-value-negative";

  return (
    <div className="metric-card">
      <div className="metric-label">{label}</div>
      <div className={`metric-value ${valueClass}`}>{value}</div>
    </div>
  );
}

function formatCurrency(value: number | undefined): string {
  if (value == null) return "--";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function MetricsGrid({
  metrics,
  initialCapital,
  finalValue,
  totalSlippage,
  totalRegulatoryFees,
}: MetricsGridProps) {
  if (!metrics) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          "CAGR",
          "Sharpe Ratio",
          "Sortino Ratio",
          "Max Drawdown",
          "Volatility",
          "Best Year",
          "Worst Year",
          "Total Return",
        ].map((label) => (
          <MetricCard key={label} label={label} value="--" isPositive={null} />
        ))}
      </div>
    );
  }

  const maxDrawdownValue =
    metrics.maxDrawdown == null
      ? "--"
      : `-${Math.abs(metrics.maxDrawdown).toFixed(1)}%`;

  const showSimulatedReturns = initialCapital != null && finalValue != null;

  return (
    <div className="space-y-4">
      {/* Backtest Returns Section */}
      {showSimulatedReturns && (
        <div>
          <div className="text-xs text-surface-500 mb-2">Backtest Returns</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              label="Initial Investment"
              value={formatCurrency(initialCapital)}
              isPositive={null}
            />
            <MetricCard
              label="Final Value"
              value={formatCurrency(finalValue)}
              isPositive={
                finalValue != null && initialCapital != null
                  ? finalValue >= initialCapital
                  : null
              }
            />
            <MetricCard
              label="Regulatory Fees"
              value={formatCurrency(totalRegulatoryFees)}
              isPositive={totalRegulatoryFees != null ? false : null}
            />
            <MetricCard
              label="Total Slippage"
              value={formatCurrency(totalSlippage)}
              isPositive={totalSlippage != null ? false : null}
            />
          </div>
        </div>
      )}

      {/* Primary Metrics */}
      <div>
        {showSimulatedReturns && (
          <div className="text-xs text-surface-500 mb-2">
            Performance Metrics
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="CAGR"
            value={`${metrics.cagr?.toFixed(1) ?? "--"}%`}
            isPositive={metrics.cagr != null ? metrics.cagr >= 0 : null}
          />
          <MetricCard
            label="Sharpe Ratio"
            value={metrics.sharpe?.toFixed(2) ?? "--"}
            isPositive={metrics.sharpe != null ? metrics.sharpe >= 1 : null}
          />
          <MetricCard
            label="Sortino Ratio"
            value={metrics.sortino?.toFixed(2) ?? "--"}
            isPositive={metrics.sortino != null ? metrics.sortino >= 1.5 : null}
          />
          <MetricCard
            label="Max Drawdown"
            value={maxDrawdownValue}
            isPositive={metrics.maxDrawdown != null ? false : null}
          />
          <MetricCard
            label="Volatility"
            value={`${metrics.volatility?.toFixed(1) ?? "--"}%`}
            isPositive={null}
          />
          <MetricCard
            label="Calmar Ratio"
            value={metrics.calmar?.toFixed(2) ?? "--"}
            isPositive={metrics.calmar != null ? metrics.calmar >= 1 : null}
          />
          <MetricCard
            label="Total Return"
            value={`${metrics.totalReturn?.toFixed(1) ?? "--"}%`}
            isPositive={
              metrics.totalReturn != null ? metrics.totalReturn >= 0 : null
            }
          />
          <MetricCard
            label="Trading Days"
            value={metrics.tradingDays?.toString() ?? "--"}
            isPositive={null}
          />
        </div>
      </div>

      {/* Benchmark Comparison */}
      {(metrics.alpha != null || metrics.beta != null) && (
        <div>
          <div className="text-xs text-surface-500 mb-2 mt-4">
            vs SPY Benchmark
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              label="Alpha"
              value={`${metrics.alpha?.toFixed(2) ?? "--"}%`}
              isPositive={metrics.alpha != null ? metrics.alpha >= 0 : null}
            />
            <MetricCard
              label="Beta"
              value={metrics.beta?.toFixed(2) ?? "--"}
              isPositive={null}
            />
            <MetricCard
              label="R²"
              value={metrics.rSquared?.toFixed(2) ?? "--"}
              isPositive={null}
            />
            <MetricCard
              label="Correlation"
              value={metrics.correlation?.toFixed(2) ?? "--"}
              isPositive={null}
            />
          </div>
        </div>
      )}

      {/* Trailing Returns & Year Performance */}
      <div>
        <div className="text-xs text-surface-500 mb-2 mt-4">Period Returns</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="Trailing 1M"
            value={`${metrics.trailing1m?.toFixed(1) ?? "--"}%`}
            isPositive={
              metrics.trailing1m != null ? metrics.trailing1m >= 0 : null
            }
          />
          <MetricCard
            label="Trailing 3M"
            value={`${metrics.trailing3m?.toFixed(1) ?? "--"}%`}
            isPositive={
              metrics.trailing3m != null ? metrics.trailing3m >= 0 : null
            }
          />
          <MetricCard
            label="Best Year"
            value={`${metrics.bestYear?.toFixed(1) ?? "--"}%`}
            isPositive={metrics.bestYear != null ? metrics.bestYear >= 0 : null}
          />
          <MetricCard
            label="Worst Year"
            value={`${metrics.worstYear?.toFixed(1) ?? "--"}%`}
            isPositive={
              metrics.worstYear != null ? metrics.worstYear >= 0 : null
            }
          />
        </div>
      </div>
    </div>
  );
}
